import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { SimpleModal } from "@adsk/alloy-react-modal";
import styled, { css } from "styled-components";
import { AbsoluteCenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";

type PageProps = {
  $hide: boolean;
};

const Page = styled.iframe<PageProps>`
  border: 0;
  visibility: ${props => (props.$hide ? "hidden" : "visible")};
`;

const ModalBodyProps = {
  style: css`
    padding: 0;
  `,
};

type MessageEventListener = (e: MessageEvent) => void;
type ChannelMessageEventListener = (
  e: MessageEvent,
  port2: MessagePort
) => void;

const usePostMessage = (
  src: string,
  onMessage?: MessageEventListener,
  options?: {
    useChannelMessaging?: boolean;
    onChannelInit?: ChannelMessageEventListener;
  }
) => {
  const origin = useMemo(() => (src ? new URL(src).origin : ""), [src]);

  const messageCallback = useCallback(
    (e: MessageEvent) => {
      if (
        options?.useChannelMessaging &&
        e.ports?.length &&
        e.data === "init"
      ) {
        const port2 = e.ports[0];
        if (onMessage) {
          port2.onmessage = onMessage;
        }
        options?.onChannelInit?.(e, port2);
      } else if (e.origin === origin) {
        onMessage?.(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [origin, onMessage]
  );

  useEffect(() => {
    if (onMessage) {
      window.addEventListener("message", messageCallback);
    }
    return () => {
      if (onMessage) {
        window.removeEventListener("message", messageCallback);
      }
    };
  }, [messageCallback, onMessage]);
};

const useIframeEventHandling = (
  onLoad?: EventListener,
  onError?: EventListener
) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const currIframeElm = iframeRef.current;
    if (onLoad) {
      currIframeElm?.addEventListener("load", onLoad);
    }
    if (onError) {
      currIframeElm?.addEventListener("error", onError);
    }

    return () => {
      if (onLoad) {
        currIframeElm?.removeEventListener("load", onLoad);
      }
      if (onError) {
        currIframeElm?.removeEventListener("error", onError);
      }
    };
  }, [onLoad, onError]);
  return iframeRef;
};

const PagePopover = ({
  src,
  className = "page-popover",
  width = "100%",
  height = "100%",
  open = true,
  show = true,
  useChannelMessaging = false,
  hashUrl,
  onChannelInit,
  onMessage,
  onLoad,
  onError,
  onPopState,
}: {
  src: string;
  className?: string;
  width?: string;
  height?: string;
  open?: boolean;
  show?: boolean;
  useChannelMessaging?: boolean;
  hashUrl?: string;
  onChannelInit?: ChannelMessageEventListener;
  onMessage?: MessageEventListener;
  onLoad?: EventListener;
  onError?: EventListener;
  onPopState?: EventListener;
}) => {
  const iframeRef = useIframeEventHandling(onLoad, onError);
  usePostMessage(src, onMessage, { useChannelMessaging, onChannelInit });

  useEffect(() => {
    if (open && hashUrl) {
      window.history.pushState({}, "", `${window.location.search}#${hashUrl}`);
    }
  }, [open, hashUrl]);

  useEffect(() => {
    if (onPopState) {
      window.addEventListener("popstate", onPopState);
    }
    return () => {
      if (onPopState) {
        window.removeEventListener("popstate", onPopState);
      }
    };
  }, [onPopState]);

  const modalProps = useMemo(
    () => ({
      width,
      height,
    }),
    [width, height]
  );

  return (
    <SimpleModal
      style={{}}
      className={className}
      show={open}
      modalProps={modalProps}
      modalBodyProps={ModalBodyProps}
    >
      <Page
        data-testid="page-popover-iframe"
        ref={iframeRef}
        src={src}
        width={width}
        height={height}
        $hide={!show}
      />
      {!show && <AbsoluteCenteredSpinner />}
    </SimpleModal>
  );
};

export default PagePopover;
